import { useCallback, useEffect, useState } from "react";
import { IAlert } from "../interfaces/alert";
import {
  createInternalMerchantsExport,
  deleteInternalMerchantsExport,
  getInternalMerchantsExports,
  getMarketplacesForExport,
  IMerchantsExport,
  IMarketplaceOption,
} from "../requests/merchantsExportRequests";
import { ERROR, SUCCESS } from "../utils/constants";

const ALL_OPTION_ID = "all";
const ACTIVE_STATUSES: IMerchantsExport["status"][] = ["pending", "processing"];

export interface IUseInternalMerchantsExport {
  exports: IMerchantsExport[];
  loading: boolean;
  creating: boolean;
  blocked: boolean;
  showConfirmModal: boolean;
  alert: IAlert;
  marketplaces: IMarketplaceOption[];
  selectedMarketplaceIds: number[];
  handleMarketplaceChange: (key: string, value: string[]) => void;
  handleCreate: () => void;
  handleConfirmCreate: () => Promise<void>;
  handleCancelConfirm: () => void;
  handleDelete: (id: number) => Promise<void>;
  clearAlert: () => void;
}

export const useInternalMerchantsExport = (): IUseInternalMerchantsExport => {
  const [exports, setExports] = useState<IMerchantsExport[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [alert, setAlert] = useState<IAlert>({ showAlert: false });
  const [marketplaces, setMarketplaces] = useState<IMarketplaceOption[]>([]);
  const [selectedMarketplaceIds, setSelectedMarketplaceIds] = useState<
    number[]
  >([]);

  const clearAlert = useCallback(() => setAlert({ showAlert: false }), []);

  const fetchExports = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getInternalMerchantsExports();
      setExports(data);
    } catch (error: any) {
      setAlert({ typeOfAlert: ERROR, message: error, showAlert: true });
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMarketplaces = useCallback(async () => {
    try {
      const data = await getMarketplacesForExport();
      setMarketplaces(data);
      setSelectedMarketplaceIds(data.map(({ id }) => id));
    } catch (error: any) {
      setAlert({ typeOfAlert: ERROR, message: error, showAlert: true });
    }
  }, []);

  const handleMarketplaceChange = useCallback(
    (_key: string, value: string[]) => {
      if (value.includes(ALL_OPTION_ID)) {
        setSelectedMarketplaceIds(marketplaces.map(({ id }) => id));
      } else {
        setSelectedMarketplaceIds(value.map(Number).filter(Boolean));
      }
    },
    [marketplaces],
  );

  const hasPendingExport = exports.some(({ status }) =>
    ACTIVE_STATUSES.includes(status),
  );

  const doCreate = async (currentExports: IMerchantsExport[]) => {
    setCreating(true);
    clearAlert();
    try {
      const activeExports = currentExports.filter(({ status }) =>
        ACTIVE_STATUSES.includes(status),
      );
      await Promise.all(
        activeExports.map(({ id }) => deleteInternalMerchantsExport(id)),
      );
      await createInternalMerchantsExport(selectedMarketplaceIds);
      setBlocked(true);
      setAlert({
        typeOfAlert: SUCCESS,
        message:
          "Exportación solicitada. El archivo estará disponible en los próximos minutos.",
        showAlert: true,
      });
      await fetchExports();
    } catch (error: any) {
      setAlert({ typeOfAlert: ERROR, message: error, showAlert: true });
    } finally {
      setCreating(false);
    }
  };

  const handleCreate = () => {
    if (!selectedMarketplaceIds.length) {
      setAlert({
        typeOfAlert: ERROR,
        message: "Seleccioná al menos un marketplace para exportar.",
        showAlert: true,
      });
      return;
    }
    if (hasPendingExport) {
      setShowConfirmModal(true);
    } else {
      doCreate(exports);
    }
  };

  const handleConfirmCreate = async () => {
    setShowConfirmModal(false);
    await doCreate(exports);
  };

  const handleCancelConfirm = () => setShowConfirmModal(false);

  const handleDelete = async (id: number) => {
    try {
      await deleteInternalMerchantsExport(id);
      setExports((prev) => prev.filter((e) => e.id !== id));
    } catch (error: any) {
      setAlert({ typeOfAlert: ERROR, message: error, showAlert: true });
    }
  };

  useEffect(() => {
    fetchMarketplaces();
    fetchExports();
  }, [fetchMarketplaces, fetchExports]);

  return {
    exports,
    loading,
    creating,
    blocked,
    showConfirmModal,
    alert,
    marketplaces,
    selectedMarketplaceIds,
    handleMarketplaceChange,
    handleCreate,
    handleConfirmCreate,
    handleCancelConfirm,
    handleDelete,
    clearAlert,
  };
};
